/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

import {
  PLAN_LABELS,
  utmEventParams,
  generateOrgSlug,
  onboardingDestination,
  parseUtmAttribution,
  parseOnboardingPlanIntent,
  PLATFORM_BRAND_NAME,
} from '@aglyn/aglyn'
import {
  trackEvent,
  trackEventBeforeNavigation,
} from '@aglyn/aglyn/app-utils/analytics-events'
import { reportPlatformAdConversion } from '@aglyn/aglyn/app-utils/platform-ad-conversions'
import { platformAdvertisingAllowed } from '@aglyn/aglyn/app-utils/platform-visitor-consent'
import type { AuthResultError } from '@aglyn/shared-data-enums'
import {
  FIELD_SCHEMA_EMAIL,
  FIELD_SCHEMA_FIRST_NAME,
  FIELD_SCHEMA_LAST_NAME,
  FIELD_SCHEMA_ORGANIZATION_NAME,
  FIELD_SCHEMA_PASSWORD,
  FIELD_SCHEMA_PASSWORD_CONFIRM,
} from '@aglyn/shared-data-forms'
import { useSearchParams } from 'next/navigation'
import { AppLink, useLoading } from '@aglyn/shared-ui-jsx'
import type { FormSchema } from '@aglyn/shared-ui-jsx-forms'
import { FormRenderer, simpleComponentMapper } from '@aglyn/shared-ui-jsx-forms'
import { mdiGoogle } from '@aglyn/shared-data-mdi'
import { MdiIcon } from '@aglyn/shared-ui-jsx'
import { LoadingTextComponent } from '@aglyn/shared-ui-jsx/components/loading-text.component'
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  setPersistence,
  signInWithPopup,
  signInWithRedirect,
  updateProfile,
  type UserCredential,
} from 'firebase/auth'
import { doc, setDoc, type Firestore } from 'firebase/firestore'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useAuth,
  useFirestore,
  useSigninCheck,
} from '@aglyn/tenant-feature-instance'
import AuthErrorAlertComponent from '../../../components/auth-error-alert.component'
import AuthFormTemplateComponent from '../../../components/auth-form-template.component'
import AuthFormComponent from '../../../components/auth-form.component'
import { AuthConsentCheckbox } from '../../../components/auth-legal-consent.component'
import { AuthMarketingOptIn } from '../../../components/auth-marketing-opt-in.component'
import AuthenticatingLayout from '../../../components/layouts/authenticating.layout'
import useDelegateWorkspaceSignIn from '../../../hooks/use-delegate-workspace-signin'
import useGoogleRedirectResult from '../../../hooks/use-google-redirect-result'
import { LEGAL_DOCUMENT_VERSION } from '../../../constants/legal-documents'
import { authSignInHost } from '../../../utils/auth-delegation'
import {
  clearLegalConsent,
  consumeLegalConsent,
  isNewAccount,
  markLegalConsent,
  postLegalAcceptance,
} from '../../../utils/legal-consent'
import hardNavigate from '../../../utils/hard-navigate'
import { markInteractiveSignIn } from '../../../utils/interactive-signin'
import {
  clearMarketingOptIn,
  consumeMarketingOptIn,
  markMarketingOptIn,
  postPlatformMarketingConsent,
} from '../../../utils/marketing-opt-in'
import {
  createSignUpWorkspace,
  rememberPendingSignUpWorkspace,
} from '../../../utils/signup-workspace'
import { rememberOnboardingPlanIntent } from '../../../utils/onboarding-plan-intent'
import { rememberSignUpCampaign } from '../../../utils/signup-campaign'
import { rememberAccountAcquisition } from '../../../utils/account-acquisition'
import { consumeConsentBounce } from '../../../utils/consent-bounce'
import isMobileBrowser from '../../../utils/is-mobile-browser'
import { createGoogleOAuthProvider } from '../../../utils/oauth-providers'
import { aimAuthAtPool } from '../../../utils/pooled-custom-token'
import guardPopupLoading from '../../../utils/popup-loading-guard'
import { WORKSPACE_DOMAIN } from '../../../constants/workspace-domain'

// Chooser forced on (AGL-1415): signing UP is the worst place to silently
// reuse the device's account — it decides which identity owns the new org.
const googleOAuthProvider = createGoogleOAuthProvider()

/**
 * The org name has to survive becoming a workspace address (AGL-1115).
 *
 * `/api/orgs/create` derives the slug with `generateOrgSlug` and rejects the
 * request when nothing usable is left — reserved words like "workspace",
 * "admin" or "api", anything under two characters, and names that are all
 * punctuation or emoji. `provisionSignUpOrg` is best-effort, so that 400 was
 * swallowed and the person landed on the workspace picker having typed a name
 * and been given nothing, with no idea why.
 *
 * Checked here rather than on the shared field: Manage Account collects the
 * same "organization name" as free text about the person's employer, and it
 * never becomes a subdomain. Only signup has this constraint.
 */
const signupOrgNameField = {
  ...FIELD_SCHEMA_ORGANIZATION_NAME,
  helperText: `This becomes your workspace address, e.g. acme-inc.${WORKSPACE_DOMAIN}`,
  validate: [
    ...(FIELD_SCHEMA_ORGANIZATION_NAME.validate ?? []),
    (value: unknown) =>
      !String(value ?? '').trim() || generateOrgSlug(String(value ?? ''))
        ? undefined
        : 'Pick a different name — this one can’t be used as a workspace ' +
          'address. Try adding a word, and avoid reserved names like ' +
          '“admin” or “workspace”.',
  ],
} as typeof FIELD_SCHEMA_ORGANIZATION_NAME

const formSchema: FormSchema = {
  fields: [
    FIELD_SCHEMA_FIRST_NAME,
    FIELD_SCHEMA_LAST_NAME,
    // Collected here so a new account lands in a ready workspace instead of
    // an empty chooser (AGL-1115). It is the org's display name; the slug is
    // derived server-side by `/api/orgs/create`, so nobody has to think about
    // URLs while signing up.
    signupOrgNameField,
    FIELD_SCHEMA_EMAIL,
    FIELD_SCHEMA_PASSWORD,
    FIELD_SCHEMA_PASSWORD_CONFIRM,
  ],
}

/**
 * Keep the name this form REQUIRES (AGL-1127).
 *
 * Both fields are `isRequired`, so no one gets an account without typing
 * them — and nothing was done with either. Sign-up created the auth user from
 * the email and password and dropped the rest on the floor, leaving the
 * account with no `displayName` and no `users/{uid}` profile doc at all. The
 * user then opened Manage Account → Basic info, found the name they had just
 * been made to enter blank, and had to type it a second time.
 *
 * Both destinations matter: rosters, comments and the app bar read the auth
 * `displayName`, while the profile doc is what Basic info edits. Best-effort
 * on purpose — the account exists and the user is signed in by this point, so
 * a failed prefill must not surface as a failed sign-up. The session-cookie
 * route re-seeds the profile from `displayName` on the next sign-in anyway.
 */
async function persistSignUpProfile(
  firestore: Firestore,
  credential: UserCredential,
  values: Record<string, unknown>,
): Promise<void> {
  const firstName = String(values[FIELD_SCHEMA_FIRST_NAME.name] ?? '').trim()
  const lastName = String(values[FIELD_SCHEMA_LAST_NAME.name] ?? '').trim()
  if (!firstName && !lastName) return
  try {
    await Promise.all([
      setDoc(
        doc(firestore, 'users', credential.user.uid),
        { firstName, lastName },
        { merge: true },
      ),
      updateProfile(credential.user, {
        displayName: [firstName, lastName].filter(Boolean).join(' '),
      }),
    ])
  } catch (error) {
    console.error('sign-up profile write failed', error)
  }
}

/**
 * Provision the workspace, for a door whose account can already use one
 * (AGL-1115 / AGL-1117).
 *
 * Best-effort by contract, like the profile write above it: the account
 * exists and the user is signed in by this point, so a failed org create must
 * not surface as a failed sign-up. It returns `null` and the caller falls
 * back to the workspace picker — which is exactly where signup used to land
 * everyone, so the worst case is the old behaviour.
 *
 * Silent on failure, deliberately (AGL-1942). Every door that still reaches
 * this one collected no name and had one DERIVED for it, and the AGL-1523
 * notice quotes the name back — "we couldn't create your workspace X" — which
 * only reads as an answer when X is what somebody entered. Apologising for a
 * workspace nobody asked for, by a name nobody chose, is worse than the
 * picker. The door that DOES collect a name no longer provisions here at all
 * (AGL-2590); its failures are raised on the workspace chooser, which is
 * where its workspace is now created.
 */
async function provisionSignUpOrg(
  credential: UserCredential,
  orgName: string,
): Promise<string | null> {
  const name = orgName.trim()
  if (!name) return null
  const { slug } = await createSignUpWorkspace(credential.user, name)
  if (!slug) return null
  /*
   * Activation (AGL-1561). The workspace auto-provisioned at signup is a
   * real org creation and is counted like any other — a failed provision
   * falls through to the picker uncounted, which is what makes "orgs
   * created" match reality.
   *
   * Awaited, through the navigation-safe door (AGL-2587). The caller
   * `hardNavigate`s the moment this resolves, and the console's transport is
   * Firebase `logEvent`, which awaits the SDK's initialization promise
   * before it reaches gtag — on the first page of a brand-new signup
   * session that promise is still pending, so the continuation was scheduled
   * behind a full document teardown and never ran. Measured, not inferred:
   * nine workspaces exist in Firestore and `org_created` had arrived in the
   * property zero times ever, while the one signup of 2026-09-04 delivered
   * `sign_up` and `login` from this same page, in the same minute, through
   * the same transport. Two events out and one gone is a difference no
   * consent state or tag setting can produce — only the navigation can.
   * Same mechanism, and same remedy, as the checkout redirect in AGL-1580.
   */
  await trackEventBeforeNavigation('org_created', {})
  return slug
}

/**
 * The workspace name for a door that collected none (AGL-1942).
 *
 * Derived exactly as `ensureOrgForUser` derives it server-side — display
 * name, else the email local part — so the two paths cannot disagree about
 * what a personal workspace is called. It is renameable afterwards, and it is
 * the same name the very next click would have produced anyway: the picker's
 * own empty state offers "create your first site", which auto-provisions the
 * workspace through exactly that helper.
 */
function derivePersonalOrgName(user: UserCredential['user']): string {
  return (
    user.displayName?.trim() || user.email?.split('@')[0]?.trim() || ''
  )
}

/**
 * The ONE landing routine every sign-up door goes through (AGL-1942).
 *
 * AGL-1115 collected an organization name on the form so a new account would
 * land in a ready workspace instead of an empty chooser — but only the
 * email/password door submits a form, so the Google doors kept the behaviour
 * the issue was filed to remove. AGL-1117 then provisioned on the Google
 * popup door as well, but only for a visitor who had clicked a plan CTA on
 * the marketing site; ordinary Google sign-ups, and every mobile one, still
 * got the picker.
 *
 * ## The verified fork (AGL-2590)
 *
 * The routine now asks whether the account can USE a workspace before it
 * creates one. `/api/auth/session` refuses to mint a session cookie while
 * `email_verified` is false, so a workspace made for an unverified account is
 * one nobody can open — it exists only to hold a permanent claim on a
 * workspace address that nobody has shown belongs to them. The AGL-1115
 * landing is delivered on the first usable session, which is after
 * verification either way, so nothing is lost by waiting.
 *
 * Google and SSO create accounts that are verified on arrival: there is
 * nothing to wait for, and they provision here exactly as before. The
 * password door holds its name with the account instead, and the workspace
 * chooser applies it on the first verified session.
 *
 * A hard navigation on purpose: the org is brand new, so the whole chrome
 * (switcher, plan badge, nav) has to resolve against it rather than re-using
 * the pre-signup tree. It also has to beat the auth layout, which pushes a
 * signed-in visitor to `/` the moment the credential lands.
 *
 * No slug means the create was refused; the caller falls through to the
 * workspace picker, which is where sign-up used to land everyone — so the
 * worst case is the old behaviour.
 */
async function provisionAndLandSignUp(
  firestore: Firestore,
  credential: UserCredential,
  orgName: string,
  planIntent: Parameters<typeof onboardingDestination>[1],
  nameWasTyped: boolean,
): Promise<void> {
  if (!credential.user.emailVerified) {
    await rememberPendingSignUpWorkspace(firestore, credential.user.uid, {
      name: orgName,
      nameWasTyped,
    })
    return
  }
  const slug = await provisionSignUpOrg(credential, orgName)
  if (slug) hardNavigate(onboardingDestination(slug, planIntent))
}

function SignUp() {
  const { queueLoading, loading } = useLoading()
  const firebaseAuth = useAuth()
  const firestore = useFirestore()
  const [error, setError] = useState<AuthResultError>(null)
  // Signups feature lockdown (AGL-1510). The server chokepoints (the mint's
  // new-account gate, the acceptance recorder) are the enforcement; this is
  // the HONEST DOOR — the page says signups are paused instead of letting
  // someone fill the form and hit a mystery refusal. Best-effort: a failed
  // status read means "not paused" and the server gates still hold.
  const [signupsPaused, setSignupsPaused] = useState<{
    message: string
  } | null>(null)
  useEffect(() => {
    let cancelled = false
    fetch('/api/lockdown-status?feature=signups')
      .then(async (response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (cancelled || !payload?.locked) return
        setSignupsPaused({
          message: String(
            payload.message ?? 'New signups are temporarily paused.',
          ),
        })
      })
      .catch(() => {
        // Unreachable status surface = fail open; the server gates decide.
      })
    return () => {
      cancelled = true
    }
  }, [])
  // Account creation is contract formation, so both sign-up flows (email and
  // Google) require affirmative agreement to the Terms and Privacy Policy.
  const [consented, setConsented] = useState(false)
  const [consentError, setConsentError] = useState(false)
  // The optional product-updates opt-in (AGL-3185). Separate state from the
  // terms acceptance above, because it is a separate act: never required,
  // never pre-ticked, and an unticked box records nothing at all.
  const [marketingOptIn, setMarketingOptIn] = useState(false)
  // The plan a visitor picked on the marketing pricing page (AGL-1117):
  // `/signup?plan=pro&interval=year`. Parsed once and defensively — the
  // contract is with a site we cannot deploy in lockstep with, so a bad
  // param degrades to ordinary signup rather than breaking it.
  const searchParams = useSearchParams()
  const planIntent = useMemo(
    () => parseOnboardingPlanIntent(searchParams),
    [searchParams],
  )
  // Which campaign produced this signup (AGL-1731) — the same hop, the same
  // defensive posture. Parsed once, from an ALLOWLIST of three `utm_` keys,
  // so the rest of whatever a marketing link carries never reaches either
  // exit. See `utm-attribution.ts` for why the allowlist is the privacy
  // mechanism rather than a convenience.
  const campaign = useMemo(
    () => parseUtmAttribution(searchParams),
    [searchParams],
  )
  // The GA4 params, memoised so the redirect hook below is not handed a new
  // object every render.
  const utmParams = useMemo(
    () => utmEventParams(campaign),
    [campaign],
  )
  // Org workspace subdomains can't run OAuth — hand sign-in to the auth
  // host and skip the local form/redirect-result entirely (AGL-465).
  const delegation = useDelegateWorkspaceSignIn('signup')
  // Mobile browsers sign in via redirect (AGL-462); this completes the
  // round-trip when Google sends the user back here.
  //
  // The account is created DURING that round-trip, so this is where a mobile
  // sign-up's acceptance has to be recorded — the click that consented
  // happened on a page that no longer exists (AGL-1497). The marker is what
  // carries the tick across; consuming it here is also what stops a stale one
  // from consenting on behalf of some later attempt.
  const handleRedirectCredential = useCallback(
    async (credential: UserCredential) => {
      // Read before the legal gate below, so a tick is never left lying in
      // storage for a later attempt whatever that gate decides (AGL-3185).
      const optedIn = consumeMarketingOptIn()
      if (!consumeLegalConsent()) {
        // Reached only if the redirect completed without this tab having
        // consented — the gate below makes that unreachable through the UI,
        // so it is worth saying out loud rather than recording an acceptance
        // nobody gave.
        console.error('sign-up redirect completed without recorded consent')
        return
      }
      await postLegalAcceptance(
        credential.user,
        LEGAL_DOCUMENT_VERSION,
        'signup-google-redirect',
      )
      // The product-updates tick this redirect carried (AGL-3185). Recorded
      // for a returning account too: the person ticked the box, and the
      // record is about that act, not about whether the account is new.
      if (optedIn) {
        await postPlatformMarketingConsent(credential.user, 'granted', 'console-signup')
      }
      // Where the visit that became this account began (AGL-3289), before
      // the workspace below is created: it copies its creator's record at
      // birth. Above the new-account return, like the popup door: an account
      // /signin created and bounced here for consent is NOT new to Firebase
      // by now, and the server — which reads the auth record's creation time
      // — is what decides whether it still counts, not this credential.
      await rememberAccountAcquisition(credential.user)
      // The mobile door provisions the workspace too (AGL-1942). It used to
      // record the acceptance and stop, so a phone sign-up — the majority of
      // them — reached the picker no matter what: not even the AGL-1117 plan
      // CTA provisioned here, because that lived in the popup's `.then`,
      // which a redirect never reaches.
      //
      // `isNewAccount` because "sign in with Google" and "sign up with
      // Google" are the same call: an existing customer returning through
      // this page must not be handed a surprise second workspace. An account
      // /signin created and bounced here for consent is not new to Firebase
      // any more, and is still signing up — /signin marked it (AGL-3424).
      if (
        !consumeConsentBounce(credential.user.uid) &&
        !isNewAccount(credential)
      ) {
        return
      }
      // Same reason the popup path remembers it (AGL-1535): the intent has to
      // outlive this page, and awaiting matters because the provision below
      // can end in a hard navigation that tears the write down mid-flight.
      await rememberOnboardingPlanIntent(
        firestore,
        credential.user.uid,
        planIntent,
      )
      // The redirect door is the MAJORITY door — most sign-ups are on a
      // phone — so leaving it out would attribute the minority of them
      // (AGL-1731/AGL-1942).
      await rememberSignUpCampaign(firestore, credential.user.uid, campaign)
      await provisionAndLandSignUp(
        firestore,
        credential,
        derivePersonalOrgName(credential.user),
        planIntent,
        false,
      )
    },
    [firestore, planIntent, campaign],
  )
  useGoogleRedirectResult(
    'sign_up',
    setError,
    delegation === 'off',
    handleRedirectCredential,
    // The redirect door fires its own `sign_up` inside the hook — the page
    // that started the flow is gone by then — so the campaign has to be
    // handed across rather than read there (AGL-1731).
    utmParams,
  )
  // Hold the loading splash during the post-auth redirect window instead of
  // flashing the form back at the user (AGL-476).
  const { data: signInCheckResult } = useSigninCheck()
  const signedIn = signInCheckResult?.signedIn === true

  const handleSignUp = useCallback(
    async (values?: any) => {
      if (loading) return
      // All four doors funnel through here (password, Google popup, Google
      // redirect) or land here via the /signin isNewUser bounce — one guard
      // covers them. Server-side, the mint refuses the account anyway; this
      // stops the doomed attempt before an account gets half-created.
      if (signupsPaused) return
      // Gate both the email/password submit and the Google button on consent.
      if (!consented) {
        setConsentError(true)
        return
      }
      if (error) setError(null)
      const dequeueLoading = queueLoading()
      // Popup flows can wedge the overlay if the popup handle is severed
      // and the SDK never rejects — see guardPopupLoading (AGL-459).
      const releaseGuard = values
        ? undefined
        : guardPopupLoading(dequeueLoading)
      // Flag the interactive sign-in BEFORE it starts so it survives the
      // mobile redirect round-trip; the session hook mints the shared
      // cookie on return instead of validating a stale one (AGL-463).
      markInteractiveSignIn()
      // Carry the tick across the mobile redirect, which unmounts this page
      // before the account exists (AGL-1497). Set here, after the gate above,
      // so the marker can only ever mean "this person consented".
      markLegalConsent()
      // The product-updates tick rides the same way (AGL-3185) — and only
      // when it was given: an unticked box sets nothing, so a stale marker
      // cannot opt somebody in.
      if (marketingOptIn) markMarketingOptIn()
      // A self-signup belongs in the PROJECT pool, and `auth.tenantId` is
      // sticky instance state that `/sso` sets and nothing clears (AGL-1993).
      // Worse here than on `/signin`: a refusal would at least be visible,
      // whereas `createUserWithEmailAndPassword` on a tenanted instance
      // SUCCEEDS and provisions the account inside that GCIP tenant, where
      // project-level `listUsers()` cannot see it — the AGL-1962 phantom
      // shape, created by an ordinary signup.
      aimAuthAtPool(firebaseAuth, null)
      await setPersistence(firebaseAuth, browserLocalPersistence)
        .then(() => {
          if (values) {
            return createUserWithEmailAndPassword(
              firebaseAuth,
              values[FIELD_SCHEMA_EMAIL.name],
              values[FIELD_SCHEMA_PASSWORD.name],
            )
          }
          // Mobile popups become tabs whose result never reaches the SDK
          // (AGL-462) — the redirect flow is the only reliable path there.
          // The overlay stays queued until the browser navigates away.
          return isMobileBrowser()
            ? signInWithRedirect(firebaseAuth, googleOAuthProvider)
            : signInWithPopup(firebaseAuth, googleOAuthProvider)
        })
        .then(async (credential) => {
          // `method` is a LITERAL per door, not `credential.providerId`
          // (AGL-1561). Firebase derives `providerId` from the Identity
          // Toolkit response, and the signUp/verifyPassword responses carry
          // no provider — so the email/password door was reporting
          // `method: null`, i.e. the one param that makes `sign_up` worth
          // having was empty for the commonest door.
          //
          // Reaching this `.then` at all means desktop: the mobile redirect
          // unmounts this page before the account exists, and is reported as
          // `google_redirect` from `useGoogleRedirectResult`.
          //
          // A Google sign-up that arrived via `?consent=required` is the
          // fourth AGL-1497 door — the account was created on /signin, stood
          // down for want of consent, and bounced here. It is counted HERE,
          // once, rather than at the bounce: the /signin account is
          // deliberately unusable until this consent happens, so counting it
          // there would inflate signups with people who never came back, and
          // counting it in both places would double every one who did.
          trackEvent('sign_up', {
            method: values
              ? 'password'
              : searchParams?.get('consent') === 'required'
                ? 'google_signin'
                : 'google_popup',
            // Which campaign produced this account (AGL-1731). Spread rather
            // than assigned, so an organic signup carries no campaign keys at
            // all instead of three empty ones.
            ...utmParams,
          })
          /*
           * The Google Ads conversion, beside the GA4 event and never instead
           * of it (AGL-1152).
           *
           * A GA4 key event imported into Ads lands as a conversion ACTION
           * without creating a conversion GOAL, and only a goal can be bid on
           * or reported in the Conversions column. This tag-fired one is what
           * makes `Sign-ups` a goal at all.
           *
           * Fired from the BROWSER rather than the server path `purchase`
           * uses: an Ads website conversion is matched to the ad click through
           * the GCLID the tag holds, which no webhook has.
           *
           * Gated on this visitor's advertising verdict, passed in rather than
           * read inside the reporter — and it degrades to "not sent" when no
           * tag has booted, which is precisely the visitor who refused. A
           * signup must never fail because a conversion could not be reported.
           */
          reportPlatformAdConversion('signup', platformAdvertisingAllowed(), {
            // Enhanced conversions (AGL-2683): the address the account was
            // just created with, hashed by the tag before it leaves the
            // browser, so Ads can still match this sign-up to the click when
            // the click cookie did not make the trip. Same consent door as
            // the event — a refused visitor's address reaches nothing.
            userData: { email: credential.user.email },
          })
          // Record the acceptance FIRST (AGL-1497). Both branches below can
          // end in `window.location.assign`, which tears this page down — a
          // record started after that navigation is a record that sometimes
          // does not happen, and "sometimes" is not a thing you want to say
          // about the evidence a contract rests on.
          //
          // The marker is consumed rather than left behind: on desktop the
          // popup never unmounts the page, so nothing else would clear it.
          consumeLegalConsent()
          await postLegalAcceptance(
            credential.user,
            LEGAL_DOCUMENT_VERSION,
            values ? 'signup-password' : 'signup-google',
          )
          // Remember the plan the visitor picked, on the ACCOUNT (AGL-1535).
          //
          // Everything below carries the intent on the URL, and that works
          // until the email-verification wall: the password door's account is
          // always unverified at this moment, so the app layout bounces the
          // billing destination to /verify-email, and the verified return
          // lands on `/` with nothing on it. Worse, the click that verifies
          // routinely happens in a different browser — on a phone, in whatever
          // the mail client opens — where no browser-local marker exists at
          // all. `users/{uid}` is the surface signup already writes and the
          // only one that survives both.
          //
          // Awaited for the same reason the acceptance above is: both branches
          // below can end in `window.location.assign`, which tears this page
          // down mid-write. Best-effort inside — a failed remember must not
          // read as a failed sign-up.
          await rememberOnboardingPlanIntent(
            firestore,
            credential.user.uid,
            planIntent,
          )
          // And where the account came FROM (AGL-1731), on the same document
          // and for the same reason: the hit that just fired answers "how
          // many signups did the campaign produce", and nothing else in the
          // system can answer "how much revenue did it produce" — that
          // arrives weeks later from a Stripe webhook with no browser and no
          // memory of a URL. Awaited alongside the intent because the
          // provision below can end in a hard navigation.
          await rememberSignUpCampaign(firestore, credential.user.uid, campaign)
          // Where the visit began, first touch and all (AGL-3289) — before the
          // provision below, whose workspace copies its creator's record. The
          // server decides from the verified token whether this account is
          // new, so a returning Google account through this door writes
          // nothing.
          await rememberAccountAcquisition(credential.user)
          // Only the email/password branch has form values to keep; the
          // Google branches carry their name on the token, and the session
          // route seeds from that (AGL-1127).
          if (values) await persistSignUpProfile(firestore, credential, values)
          // The product-updates opt-in, when it was given (AGL-3185). After
          // the profile write, so the record can carry the name the form
          // collected; before the provision below, which can end in a hard
          // navigation. The marker is consumed so nothing is left in storage,
          // and the state is read beside it: this page never unmounted, so
          // the tick is still here even where storage refused the marker.
          const carriedOptIn = consumeMarketingOptIn()
          if (carriedOptIn || marketingOptIn) {
            await postPlatformMarketingConsent(credential.user, 'granted', 'console-signup')
          }
          // Settle the workspace — from EVERY door (AGL-1942), through the
          // one routine. A verified account (Google, SSO) gets it created and
          // lands in it; the password door's account cannot use one yet, so
          // the routine holds the name against the account and the workspace
          // chooser creates it on the first verified session (AGL-2590).
          //
          // The form door supplies the name it collected (AGL-1115). The
          // Google doors have no form, so the name is derived the way the
          // server derives it; provisioning was previously gated on the
          // visitor having clicked a marketing plan CTA (AGL-1117), which is
          // a minority of sign-ups, so the ordinary Google visitor got the
          // empty chooser this was all built to remove.
          //
          // `isNewAccount` guards the Google doors only: `signInWithPopup` is
          // a sign-in-or-sign-up and does not ask which you meant, so an
          // existing customer clicking Google here must not be handed a
          // second workspace. `createUserWithEmailAndPassword` can only ever
          // have created one.
          //
          // The /signin consent bounce is the one Google sign-up the flag
          // misses: Firebase has known that account since /signin, so it is
          // not "new" here, and it is still this person's sign-up. /signin
          // marked it for its uid (AGL-3424).
          const typedName = values
            ? String(values[FIELD_SCHEMA_ORGANIZATION_NAME.name] ?? '').trim()
            : ''
          const bouncedForConsent =
            !values && consumeConsentBounce(credential.user.uid)
          if (values || bouncedForConsent || isNewAccount(credential)) {
            await provisionAndLandSignUp(
              firestore,
              credential,
              typedName || derivePersonalOrgName(credential.user),
              planIntent,
              Boolean(typedName),
            )
          }
        })
        .catch((error) => {
          console.error(error)
          // A failed attempt must not leave a tick lying in sessionStorage
          // for the next one to pick up (AGL-1497) — either tick (AGL-3185).
          clearLegalConsent()
          clearMarketingOptIn()
          setError({
            ...error,
            credential: GoogleAuthProvider.credentialFromError(error),
          })
        })
        .finally(() => {
          releaseGuard?.()
          dequeueLoading()
        })
    },
    [
      consented,
      error,
      firebaseAuth,
      firestore,
      loading,
      marketingOptIn,
      planIntent,
      queueLoading,
      searchParams,
      signupsPaused,
    ],
  )

  const handleConsentChange = useCallback((next: boolean) => {
    setConsented(next)
    if (next) setConsentError(false)
  }, [])

  const handleFormSubmit = useCallback(
    async (values) => {
      await handleSignUp(values)
    },
    [handleSignUp],
  )
  const handleGoogleButtonClick = useCallback(async () => {
    await handleSignUp()
  }, [handleSignUp])

  if (delegation === 'redirecting') {
    // Bouncing to the auth host (AGL-465) — no local form or OAuth here.
    return (
      <AuthFormComponent
        headingTop={'Redirecting'}
        headingBottom={'Taking you to sign in'}
        headingBottomProps={{
          sx: { pb: 4 },
          component: LoadingTextComponent,
        }}
        headingAfter={<CircularProgress color="primary" />}
      />
    )
  }
  if (signedIn) {
    // Authenticated — the layout is about to route away. Hold the loading
    // screen so the form doesn't flash back (AGL-476).
    return (
      <AuthFormComponent
        headingTop={'Signing in'}
        headingBottom={'One moment'}
        headingBottomProps={{
          sx: { pb: 4 },
          component: LoadingTextComponent,
        }}
        headingAfter={<CircularProgress color="primary" />}
      />
    )
  }
  if (delegation === 'stopped') {
    // Delegation kept coming back session-less (AGL-467) — surface an escape
    // instead of an endless spinner.
    return (
      <AuthFormComponent
        headingTop={'Sign-in didn’t complete'}
        headingBottom={'We couldn’t establish your session on this workspace.'}
        paperAfter={
          <Typography component="div" variant="body2">
            <Link href={`https://${authSignInHost()}/signin`}>
              {'Sign in again'}
            </Link>
          </Typography>
        }
      />
    )
  }

  return (
    <AuthFormComponent
      paperTop={
        <Typography
          component="div"
          variant="body2"
          sx={{
            alignSelf: 'flex-end',
          }}
        >
          <AppLink href="/signin">{'Sign in'}</AppLink>
        </Typography>
      }
      headingTop={'Sign up'}
      headingBottom={
        // Say which plan they picked (AGL-1117). The deep link carried it all
        // the way to checkout already, but the form said nothing — so someone
        // who clicked "Get Pro, billed yearly" on the pricing page landed on a
        // generic "Create a new Aglyn Account" with no sign their choice had
        // survived the click. Cheap to doubt, and expensive to doubt on the
        // one page where abandoning costs a sale.
        planIntent
          ? planIntent.contactSales
            ? `Create your account — we’ll get you set up on ${PLAN_LABELS[planIntent.plan]}`
            : `Create your account to start on ${PLAN_LABELS[planIntent.plan]}, billed ${
                planIntent.interval === 'year' ? 'yearly' : 'monthly'
              }`
          : `Create a new ${PLATFORM_BRAND_NAME} Account`
      }
      paperAfter={
        <Typography component="div" variant="body2">
          {'Already have an account? '}
          <AppLink href="/signin">{'Sign in'}</AppLink>
        </Typography>
      }
    >
      <FormRenderer
        FormTemplate={AuthFormTemplateComponent}
        // Between the fields and Next (AGL-3291): the terms box is what Next
        // checks, so it has to be met before Next is pressed. Still above
        // "Or sign up with", so it reads as gating the Google door too.
        FormTemplateProps={{
          beforeSubmit: (
            // `useFlexGap`: a margin-spaced Stack zeroes its children's
            // margins, and the label's negative left margin is what puts
            // each box on the fields' edge.
            <Stack spacing={1.5} useFlexGap>
              {searchParams?.get('consent') === 'required' ? (
                // Bounced from /signin, where Google had just created this
                // account without ever showing the Terms (AGL-1497). Say so,
                // or the redirect looks like the sign-in simply failed.
                <Alert severity="info">
                  {'Looks like you’re new here — please accept the Terms of Service ' +
                    'and Privacy Policy to finish setting up your account.'}
                </Alert>
              ) : null}
              <AuthConsentCheckbox
                checked={consented}
                onChange={handleConsentChange}
                error={consentError}
              />
              {/* Below the required acceptance and never part of it
                  (AGL-3185): the terms gate the sign-up, this gates nothing. */}
              <AuthMarketingOptIn
                checked={marketingOptIn}
                onChange={setMarketingOptIn}
              />
            </Stack>
          ),
        }}
        componentMapper={simpleComponentMapper}
        onSubmit={handleFormSubmit}
        schema={formSchema}
        subscription={{ values: true }}
        clearOnUnmount
      />
      {signupsPaused ? (
        // The honest notice (AGL-1510): a paused signup explains itself.
        // The /signin Google door's isNewUser bounce lands here too, so a
        // brand-new Google account sees this rather than a failed sign-in.
        <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
          {`New signups are temporarily paused — ${signupsPaused.message}`}
        </Alert>
      ) : null}
      <AuthErrorAlertComponent error={error} sx={{ mt: 2, mb: 1 }} />
      <Divider flexItem variant="middle" sx={{ my: 3 }}>
        {'Or sign up with'}
      </Divider>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          justifyContent: 'center',
          alignItems: 'stretch',
          paddingBottom: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<MdiIcon path={mdiGoogle.path} />}
          onClick={handleGoogleButtonClick}
          disabled={Boolean(signupsPaused)}
        >
          {'Google'}
        </Button>
      </Stack>
    </AuthFormComponent>
  )
}
SignUp.displayName = 'Page:SignUp'

export default SignUp
